/**
 * Stage 3: Normalization.
 * Transforms a structurally+semantically valid payload into the internal
 * form expected by existing create flows and Firestore writes.
 */

import {
  POLL_KIND,
  VOTE_ANONYMIZATION,
  VOTE_VISIBILITY,
} from "./constants.js";

let idCounter = 0;

/** Generate a simple unique ID. In production use nanoid; here we keep it dependency-free. */
function generateId() {
  idCounter += 1;
  return `opt_${Date.now()}_${idCounter}`;
}

/** Reset counter (for tests). */
export function _resetIdCounter() {
  idCounter = 0;
}

function resolveVoteVisibility(value) {
  const valid = new Set(Object.values(VOTE_VISIBILITY));
  return valid.has(value) ? value : VOTE_VISIBILITY.FULL;
}

function resolveVoteAnonymization(value) {
  const valid = new Set(Object.values(VOTE_ANONYMIZATION));
  return valid.has(value) ? value : VOTE_ANONYMIZATION.NONE;
}

function resolveHideVoterIdentities(value, voteVisibility) {
  if (voteVisibility === VOTE_VISIBILITY.FULL) return false;
  return value === true;
}

function normalizeVotePrivacy(payload) {
  const voteVisibility = resolveVoteVisibility(payload.voteVisibility);
  return {
    voteVisibility,
    voteAnonymization: resolveVoteAnonymization(payload.voteAnonymization),
    hideVoterIdentities: resolveHideVoterIdentities(
      payload.hideVoterIdentities,
      voteVisibility
    ),
  };
}

function normalizeOptions(options) {
  return (options || [])
    .filter((o) => o.label?.trim())
    .map((o, index) => ({
      id: generateId(),
      label: o.label.trim(),
      note: o.note || "",
      order: index,
    }));
}

function normalizeSettings(settings) {
  return {
    voteType: settings?.voteType || "MULTIPLE_CHOICE",
    allowMultiple: settings?.allowMultiple || false,
    maxSelections: settings?.maxSelections ?? null,
    allowWriteIn: settings?.allowWriteIn || false,
    deadlineAt: settings?.deadlineAt ? new Date(settings.deadlineAt) : null,
  };
}

function normalizeBasicPayload(payload) {
  return {
    title: (payload.title || "").trim(),
    description: (payload.description || "").trim(),
    ...normalizeVotePrivacy(payload),
    settings: normalizeSettings(payload.settings),
    options: normalizeOptions(payload.options),
    votesAllSubmitted: false,
  };
}

export function normalizeEmbeddedPoll(payload, order) {
  return {
    ...normalizeBasicPayload(payload),
    required: payload.required === true,
    order,
  };
}

function normalizeSessionPayload(payload) {
  const privacy = normalizeVotePrivacy(payload);

  return {
    title: (payload.title || "Untitled poll").trim() || "Untitled poll",
    description: (payload.description || "").trim(),
    timezone: payload.timezone || null,
    timezoneMode: payload.timezoneMode || "auto",
    questingGroup: payload.questingGroup
      ? {
          id: payload.questingGroup.id || null,
          name: payload.questingGroup.name || null,
        }
      : null,
    invitees: (payload.invitees || []).map((s) => s.trim()),
    allowLinkSharing: payload.allowLinkSharing === true,
    ...privacy,
    slots: (payload.slots || []).map((s) => ({
      start: new Date(s.startUtcIso),
      end: new Date(s.endUtcIso),
    })),
    embeddedPolls: (payload.embeddedPolls || []).map((ep, i) =>
      normalizeEmbeddedPoll(ep, i)
    ),
    votesAllSubmitted: false,
  };
}

/**
 * Normalize a validated poll upload payload into the internal form.
 *
 * @param {{ schemaVersion: string, pollKind: string, payload: object }} input
 * @returns {{ schemaVersion: string, pollKind: string, payload: object }}
 */
export function normalize(input) {
  const { schemaVersion, pollKind, payload } = input;

  if (pollKind === POLL_KIND.SESSION) {
    return {
      schemaVersion,
      pollKind,
      payload: normalizeSessionPayload(payload),
    };
  }

  const basic = normalizeBasicPayload(payload);
  basic.questingGroup = payload.questingGroup
    ? {
        id: payload.questingGroup.id || null,
        name: payload.questingGroup.name || null,
      }
    : null;

  return {
    schemaVersion,
    pollKind,
    payload: basic,
  };
}
